import {Inject, Injectable} from '@angular/core';
import {ApiBaseService} from "libs/shared-services/src/lib/api-base.service";
import {ToasterService} from "libs/shared-services/src/lib/toaster.service";
import {environment} from "../../../environments/environment";
import {UserSelfResponse} from "../../models/user-response";
import {BehaviorSubject, Observable} from "rxjs";
import { Language, LocaleService } from 'libs/shared-services/src/lib/locale.service';
import { BaseLoadingService } from 'libs/shared-services/src/lib/base-loading';
import { Product } from 'libs/shared-models/src/lib/product-type';

@Injectable({
    providedIn: 'root',
})
export class SelfDataService extends BaseLoadingService {

    private userData: BehaviorSubject<UserSelfResponse> = new BehaviorSubject<UserSelfResponse>(new UserSelfResponse());
    private lastStoredISOLanguage: string = "";

    constructor(
        @Inject('env') private environment: any,
        private apiService: ApiBaseService,
        private toasterService: ToasterService,
        private localeService: LocaleService
    ) {
        super();
        this.listenLanguageChange();
    }

    public fetchSelfDataAPI() {
        this.apiService.get(environment.API_USER_SELF).subscribe({
            next: (res: any) => {
                let data = Object.assign(new UserSelfResponse(), res);
                this.setData(data);
                this.setFinished();

                this.redirectToSelfProduct();
            },
            error: (err) => {
                this.toasterService.showError("Error", err?.error?.message);
            }
        });
    }

    private setData(data: UserSelfResponse) {
        this.userData.next(data);
    }

    public getSelfData$(): Observable<UserSelfResponse> {
        return this.userData.asObservable();
    }

    public getSelfData(): UserSelfResponse {
        return this.userData.getValue();
    }

    public setPhoneNumberApi(phone: string) {
        this.apiService.patch(environment.API_USER_SELF, {
            id: this.getSelfData().id,
            phoneNumber: phone
        }).subscribe({
            next: (res: any) => {
                this.setData(res);
                this.toasterService.showSuccess("", this.localeService.translate("restaurant_info_update_success"));
            },
            error: (err) => {
                this.toasterService.showError("Error", err?.error?.message);
            }
        });
    }

    public setLanguageApi(language: string) {
        this.apiService.patch(environment.API_USER_SELF, {
            id: this.getSelfData().id,
            userLanguage: language
        }).subscribe({
            next: (res: any) => {
                // don't show any success toast - this is more like an update for the backend to know, as the change is fully frontend
                this.setData(res);                
            },
            error: (err) => {
                // don't show anything, just log it in console if it fails
                console.error("[API request failed] - Self endpoint language update")
            }
        });
    }

    private listenLanguageChange() {
        this.localeService.getLanguage$().subscribe((lang: Language) => {            
            if (!!lang?.iso2) {
                if (!this.lastStoredISOLanguage) {
                    this.lastStoredISOLanguage = lang.iso2;
                } else {
                    if (this.lastStoredISOLanguage !== lang.iso2) {
                        this.setLanguageApi(lang.iso2);
                    }
                }
            }
        })
    }

    private redirectToSelfProduct() {
        const data = this.getSelfData();
        const product = data.product.toLowerCase();

        // ignore if product is the expected one (restaurant, as we're in the restaurant service / subdomain)
        if (product === Product.RESTAURANT) {
            return;
        } else {
            // redirect the user to the correct product
            let redirectUrl = "";
            switch (product) {
                case Product.USER_APP: {
                    redirectUrl = this.environment.PRODUCT_URLS.USER_APP;
                    break;
                }
                case Product.DRIVER: {
                    redirectUrl = this.environment.PRODUCT_URLS.DRIVER;
                    break;
                }
                case Product.ADMIN: 
                case "staff": { // role
                    redirectUrl = this.environment.PRODUCT_URLS.ADMIN;
                    break;
                }
                default: {
                    redirectUrl = this.environment.PRODUCT_URLS.LANDING_PAGE;
                    break;
                }
            }
            // Comment this line when you need to test with localhost development - otherwise you'll always get redirected to deployed version
            window.location.href = redirectUrl;
        }
    }
}

